import { PageInWP } from "@/lib/singularity/wp-page-manager/types";
import {
  SingularityBrand,
  NamedColorPalette,
  BorderRadius
} from "@/types/singularity/brand";
import { AIServer } from "@/types/singularity";
import { AIJobStateForUI } from "@/types/singularity/ai-jobs";
import { TreeNode } from "@/types/tree";
import {
  Module,
  VuexModule,
  Mutation,
  Action,
  config
} from "vuex-module-decorators";

config.rawError = true;

@Module({ name: "singularity", namespaced: true })
export default class SingularityModule extends VuexModule {
  singularityUiState: SingularityUIState = {
    singularityRightPanel: null,
    singularityLeftPanel: null,
    singularityDebugPanel: null
  };

  pagesInWP: PageInWP[] = [];

  sectionAddOrSwapPanelState: SectionAddOrSwapPanelState = {
    mode: "add",
    sectionIdToAddAfterOrSwap: null
  };

  imageEditorPanelState: ImageEditorPanelState = null;

  backgroundImageEditorPanelState: BackgroundImageEditorPanelState = null;

  contentPropertiesEditorPanelState = false;

  iconEditorPanelState: IconEditorPanelState = {
    iconId: null
  };

  textEditorPanelState: TextEditorPanelState = null;

  linkEditorPanelState: LinkEditorPanelState = null;

  aiJobs: AIJobStateForUI[] = [];

  aiServer: AIServer = {
    url: "http://localhost:3000",
    key: "licenseKey"
  };

  brand: SingularityBrand = {
    color: {
      neutral: null,
      primary: null,
      secondary: []
    },
    ui: {
      buttonRadius: "md",
      inputRadius: "md"
    }
  };

  currentPalette: CurrentPaletteState = null;

  currentFont: "body_font" | "heading_font" | null = null;

  @Mutation
  setRadius(payload: {
    buttonRadius?: BorderRadius;
    inputRadius?: BorderRadius;
  }) {
    if (payload.buttonRadius) {
      this.brand.ui.buttonRadius = payload.buttonRadius;
    }
    if (payload.inputRadius) {
      this.brand.ui.inputRadius = payload.inputRadius;
    }
  }

  @Mutation
  setCurrentFont(font: "body_font" | "heading_font" | null) {
    this.currentFont = font;
  }

  @Mutation
  setCurrentPalette(payload: {
    palette: NamedColorPalette;
    index: number | null;
  }) {
    this.currentPalette = {
      palette: payload.palette,
      index: payload.index
    };
  }

  @Mutation
  setBrand(brand: SingularityBrand) {
    console.log("Setting brand:", brand);
    this.brand = brand;
  }

  @Mutation
  setPalette(payload: {
    type: "primary" | "secondary" | "neutral";
    palette: NamedColorPalette | null;
  }) {
    switch (payload.type) {
      case "primary":
        this.brand.color.primary = payload.palette;
        break;
      case "neutral":
        this.brand.color.neutral = payload.palette;
        break;
      case "secondary":
        if (payload.palette) {
          this.brand.color.secondary.push(payload.palette);
        } else {
          this.brand.color.secondary = [];
        }
        break;
    }
  }

  @Mutation
  addSecondaryPalette(palette: NamedColorPalette) {
    this.brand.color.secondary.push(palette);
  }

  @Mutation
  removeSecondaryPalette(index: number) {
    if (index >= 0 && index < this.brand.color.secondary.length) {
      this.brand.color.secondary.splice(index, 1);
    }
  }

  @Mutation
  updateSecondaryPalette({
    index,
    palette
  }: {
    index: number;
    palette: NamedColorPalette;
  }) {
    this.brand.color.secondary.splice(index, 1, palette);
  }

  @Mutation
  setAiServer(aiServer: AIServer) {
    this.aiServer = aiServer;
  }

  @Action
  setSingularityRightPanel(panel: SingularityUIState["singularityRightPanel"]) {
    this.setSingularityUiState({
      ...this.singularityUiState,
      singularityRightPanel: panel
    });
  }

  @Action
  setSingularityLeftPanel(panel: SingularityUIState["singularityLeftPanel"]) {
    this.setSingularityUiState({
      ...this.singularityUiState,
      singularityLeftPanel: panel
    });
  }

  @Action
  setSingularityDebugPanel(panel: SingularityUIState["singularityDebugPanel"]) {
    this.setSingularityUiState({
      ...this.singularityUiState,
      singularityDebugPanel: panel
    });
  }

  @Mutation
  setSingularityUiState(singularityUiState: SingularityUIState) {
    this.singularityUiState = singularityUiState;
  }

  @Mutation
  setPagesInWP(pagesInWP: PageInWP[]) {
    this.pagesInWP = pagesInWP;
  }

  @Mutation
  setAIJobs(aiJobs: AIJobStateForUI[]) {
    this.aiJobs = aiJobs;
  }

  @Mutation
  setSectionAddOrSwapPanelState(
    sectionAddOrSwapPanelState: SectionAddOrSwapPanelState
  ) {
    this.sectionAddOrSwapPanelState = sectionAddOrSwapPanelState;
  }

  @Action
  setImageEditorPanelState(imageEditorPanelState: ImageEditorPanelState) {
    this.setImageEditorPanelState_(imageEditorPanelState);
    if (!imageEditorPanelState) {
      this.setSingularityLeftPanel("page_structure");
    }
  }

  @Mutation
  setImageEditorPanelState_(imageEditorPanelState: ImageEditorPanelState) {
    this.imageEditorPanelState = imageEditorPanelState;
  }

  @Action
  setBackgroundImageEditorPanelState(
    backgroundImageEditorPanelState: BackgroundImageEditorPanelState
  ) {
    this.setBackgroundImageEditorPanelState_(backgroundImageEditorPanelState);
    if (!backgroundImageEditorPanelState) {
      this.setSingularityLeftPanel("page_structure");
    }
  }

  @Mutation
  setBackgroundImageEditorPanelState_(
    backgroundImageEditorPanelState: BackgroundImageEditorPanelState
  ) {
    this.backgroundImageEditorPanelState = backgroundImageEditorPanelState;
  }

  @Mutation
  setContentPropertiesEditorPanelState(
    contentPropertiesEditorPanelState: boolean
  ) {
    this.contentPropertiesEditorPanelState = contentPropertiesEditorPanelState;
  }

  @Mutation
  setIconEditorPanelState(iconEditorPanelState: IconEditorPanelState) {
    this.iconEditorPanelState = iconEditorPanelState;
  }

  @Action
  setTextEditorPanelState(textEditorPanelState: TextEditorPanelState) {
    if (textEditorPanelState) {
      this.setSingularityLeftPanel("text_editor");
    } else {
      this.setSingularityLeftPanel("page_structure");
    }
    this.setTextEditorPanelState_(textEditorPanelState);
  }

  @Action
  setLinkEditorPanelState(linkEditorPanelState: LinkEditorPanelState) {
    if (linkEditorPanelState) {
      this.setSingularityLeftPanel("link_editor");
    } else {
      this.setSingularityLeftPanel("page_structure");
    }
    this.setLinkEditorPanelState_(linkEditorPanelState);
  }

  @Mutation
  setLinkEditorPanelState_(linkEditorPanelState: LinkEditorPanelState) {
    this.linkEditorPanelState = linkEditorPanelState;
  }

  @Mutation
  setTextEditorPanelState_(textEditorPanelState: TextEditorPanelState) {
    this.textEditorPanelState = textEditorPanelState;
  }
}

type SectionAddOrSwapPanelState = {
  mode: "add" | "swap";
  sectionIdToAddAfterOrSwap: null | TreeNode["id"];
};

export type SingularityUIState = {
  singularityRightPanel: null | "step1";
  singularityLeftPanel:
    | null
    | "page_structure"
    | "page_manager"
    | "brand"
    | "brand_edit_color"
    | "brand_edit_typography"
    | "settings"
    | "publish"
    | "section_add_or_swap"
    | "image_editor"
    | "background_image_editor"
    | "icon_editor"
    | "text_editor"
    | "link_editor"
    | "content_properties_editor";

  singularityDebugPanel: null | "debug";
};

type ImageEditorPanelState = {
  imageId: TreeNode["id"];
} | null;

type BackgroundImageEditorPanelState = {
  sectionId: TreeNode["id"];
} | null;

type IconEditorPanelState = {
  iconId: TreeNode["id"] | null;
};

type TextEditorPanelState = {
  elementId: TreeNode["id"];
  contentEditablePropertyPath: string;
} | null;

type LinkEditorPanelState = {
  elementId: TreeNode["id"];
  linkPropertyPaths: string[];
} | null;

type CurrentPaletteState = {
  palette: NamedColorPalette | null;
  index: null | number;
} | null;
